import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import {
  Alert,
  Box,
  Paper,
  Toolbar,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Snackbar,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import Swal from "sweetalert2";
import ActionsMenu from "./ActionsMenu";

export default function Issue() {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const isInvalidSession =
    !clientId ||
    !collegeId ||
    clientId === "null" ||
    collegeId === "null" ||
    isNaN(Number(clientId)) ||
    isNaN(Number(collegeId));

  const [idError, setIdError] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    user_type_id: "",
    academic_year: "",
    issue_type: "SINGLE",
    entered_id: "",
    user_id: null,
    books: [],
  });
  const [userTypes, setUserTypes] = useState([]);
  const [scanBookIsbn, setScanBookIsbn] = useState("");
  const [selectedBookOption, setSelectedBookOption] = useState(null);
  const [bookOptions, setBookOptions] = useState([]);
  const [loadingBookOptions, setLoadingBookOptions] = useState(false);
  const [isbnFieldError, setIsbnFieldError] = useState("");
  const [idFieldError, setIdFieldError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [rules, setRules] = useState(null);
  const [resolvedMember, setResolvedMember] = useState(null);
  const [resolvingMember, setResolvingMember] = useState(false);
  const [userIdValidated, setUserIdValidated] = useState(false);
  const [issuePageSize, setIssuePageSize] = useState(10);
  const memberRequestRef = useRef(0);

  useEffect(() => {
    if (!clientId || !collegeId || clientId === "null" || collegeId === "null") {
      setIdError("Missing or invalid client/college ID. Please log in again or contact admin.");
    } else {
      setIdError("");
    }
  }, [clientId, collegeId]);

  useEffect(() => {
    const swalStyle = document.createElement("style");
    swalStyle.innerHTML = `.swal2-z-topmost { z-index: 9999 !important; } .swal2-container { z-index: 9999 !important; }`;
    swalStyle.setAttribute("data-swal-zindex", "true");
    if (!document.head.querySelector("[data-swal-zindex]")) {
      document.head.appendChild(swalStyle);
    }
    return () => {
      if (swalStyle.parentNode) swalStyle.parentNode.removeChild(swalStyle);
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}`
      );
      const items = Array.isArray(response.data.data?.items)
        ? response.data.data.items.map((row) => {
            const userTypeObj = userTypes.find(
              (ut) => String(ut.user_type_id) === String(row.user_type_id)
            );
            const books = Array.isArray(row.issue_details)
              ? row.issue_details.map((d) => ({
                  copy_id: d.copy_id,
                  isbn: d.copy?.book?.isbn,
                  title: d.copy?.book?.title,
                  accession_no: d.copy?.accession_no,
                }))
              : [];
            return {
              ...row,
              id: row.issue_id,
              user_type: userTypeObj ? userTypeObj.user_type_name : row.user_type_id,
              student_id: row.user_id || "",
              books,
            };
          })
        : [];
      setRows(items);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to fetch issues",
        severity: "error",
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTypes = async () => {
    if (!clientId || !collegeId) return setUserTypes([]);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/user-types`
      );
      if (res.data.success && Array.isArray(res.data.data)) {
        setUserTypes(res.data.data);
      } else {
        setUserTypes([]);
      }
    } catch (err) {
      setUserTypes([]);
    }
  };

  useEffect(() => {
    if (clientId && collegeId && clientId !== "null" && collegeId !== "null") {
      fetchData();
      fetchUserTypes();
    }
  }, [clientId, collegeId]);

  const handleDownloadCsv = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}`
      );
      const items = Array.isArray(response.data.data?.items) ? response.data.data.items : [];
      if (!items.length) {
        alert("No issue data to export.");
        return;
      }
      const headers = [
        "issue_id",
        "User Type",
        "Issue Type",
        "User ID",
        "Issue Date",
        "Due Date",
        "Books",
      ];
      const csvRows = [headers.join(",")];
      items.forEach((row) => {
        const booksStr = (row.issue_details || []).map((b) => b.copy?.book?.title || "").join(";");
        csvRows.push(
          [
            row.issue_id || "",
            `"${row.user_type_id || ""}"`,
            `"${row.issue_type || ""}"`,
            `"${row.user_id || ""}"`,
            `"${row.issue_date ? new Date(row.issue_date).toLocaleDateString() : ""}"`,
            `"${row.due_date ? new Date(row.due_date).toLocaleDateString() : ""}"`,
            `"${booksStr}"`,
          ].join(",")
        );
      });
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "issues.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download issue data as CSV.");
    }
  };

  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/upload/csv`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setSnackbar({
        open: true,
        message: data.message || "CSV uploaded successfully",
        severity: "success",
      });
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Upload failed",
        severity: "error",
      });
    } finally {
      e.target.value = "";
    }
  };

  const handleAdd = () => {
    setEditing(null);
    setForm({
      user_type_id: "",
      academic_year: "",
      issue_type: "SINGLE",
      entered_id: "",
      user_id: null,
      books: [],
    });
    setOpen(true);
    setScanBookIsbn("");
    setSelectedBookOption(null);
    setUserIdValidated(false);
    setResolvedMember(null);
    setRules(null);
  };

  const handleClose = () => setOpen(false);

  const resetResolvedUser = () => {
    memberRequestRef.current += 1;
    setIdFieldError("");
    setUserIdValidated(false);
    setResolvedMember(null);
    setRules(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "entered_id") {
        next.user_type_id = "";
        next.academic_year = "";
        next.user_id = null;
      }
      if (name === "issue_type") {
        next.books = [];
      }
      return next;
    });
    if (e.target.name === "entered_id") {
      resetResolvedUser();
    }
    if (e.target.name === "issue_type" && form.entered_id) {
      resetResolvedUser();
    }
    if (e.target.name === "issue_type") {
      setScanBookIsbn("");
      setSelectedBookOption(null);
      setIsbnFieldError("");
    }
  };

  const resolveEnteredId = async (enteredId = form.entered_id, issueType = form.issue_type) => {
    const trimmedId = String(enteredId || "").trim();
    if (!trimmedId || !clientId || !collegeId) return;

    const requestId = memberRequestRef.current + 1;
    memberRequestRef.current = requestId;
    setResolvingMember(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/resolve-member/${encodeURIComponent(
          trimmedId
        )}?issue_type=${encodeURIComponent(issueType || "SINGLE")}`
      );
      if (requestId !== memberRequestRef.current) return;
      if (res.data.success && res.data.data) {
        const member = res.data.data;
        setIdFieldError("");
        setForm((prev) => ({
          ...prev,
          entered_id: trimmedId,
          user_id: member.user_id,
          user_type_id: member.user_type_id,
          academic_year: member.academic_year,
        }));
        setResolvedMember(member);
        setRules(member.rules || null);
        setUserIdValidated(true);
      } else {
        setIdFieldError("No active student or staff found for this ID.");
        setForm((prev) => ({ ...prev, user_id: null }));
        setUserIdValidated(false);
        setResolvedMember(null);
        setRules(null);
      }
    } catch (err) {
      if (requestId !== memberRequestRef.current) return;
      setIdFieldError(
        err?.response?.data?.message || "No active student or staff found for this ID."
      );
      setForm((prev) => ({ ...prev, user_id: null }));
      setUserIdValidated(false);
      setResolvedMember(null);
      setRules(null);
    } finally {
      if (requestId === memberRequestRef.current) {
        setResolvingMember(false);
      }
    }
  };

  useEffect(() => {
    if (!open || editing || !form.entered_id) return undefined;
    const validateTimer = window.setTimeout(() => {
      resolveEnteredId(form.entered_id, form.issue_type);
    }, 450);
    return () => window.clearTimeout(validateTimer);
  }, [open, editing, form.entered_id, form.issue_type]);

  const fetchBookOptions = async (searchText = "") => {
    if (!clientId || !collegeId) return;

    setLoadingBookOptions(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/book-options?search=${encodeURIComponent(
          searchText
        )}`
      );
      if (res.data.success && Array.isArray(res.data.data)) {
        setBookOptions(res.data.data);
      } else {
        setBookOptions([]);
      }
    } catch {
      setBookOptions([]);
    } finally {
      setLoadingBookOptions(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const searchTimer = window.setTimeout(() => {
      fetchBookOptions(scanBookIsbn);
    }, 350);
    return () => window.clearTimeout(searchTimer);
  }, [open, scanBookIsbn]);

  const handleBookScan = async (bookOption = selectedBookOption) => {
    setIsbnFieldError("");

    const selectedIsbn = bookOption?.isbn;
    if (!bookOption || !selectedIsbn) {
      setIsbnFieldError("Please select a book from the dropdown");
      return;
    }

    if (
      form.books.find(
        (b) => String(b.copy_id) === String(bookOption?.copy_id) || b.isbn === selectedIsbn
      )
    ) {
      setSnackbar({
        open: true,
        message: "Book already added",
        severity: "warning",
      });
      return;
    }

    // Check if book is already issued to this user
    const alreadyIssued = rows.some(
      (row) =>
        row.user_id === form.user_id &&
        row.issue_details?.some((detail) => detail.copy?.book?.isbn === selectedIsbn)
    );

    if (alreadyIssued) {
      setSnackbar({
        open: true,
        message: "This book is already issued to this user",
        severity: "error",
      });
      return;
    }

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/validate-book/${encodeURIComponent(
          selectedIsbn
        )}`
      );
      if (res.data.success && res.data.data) {
        if (res.data.data.book_status !== "AVAILABLE") {
          setSnackbar({
            open: true,
            message: `Book is ${res.data.data.book_status}. It cannot be issued now.`,
            severity: "error",
          });
          return;
        }

        if (!res.data.data.copy_id) {
          setSnackbar({
            open: true,
            message: "No available copy found for this ISBN.",
            severity: "error",
          });
          return;
        }

        setForm((prev) => ({
          ...prev,
          books: [
            ...prev.books,
            {
              isbn: res.data.data.book?.isbn || bookOption?.isbn || selectedIsbn,
              copy_id: res.data.data.copy_id || bookOption?.copy_id,
              title: res.data.data.book?.title || bookOption?.title,
              accession_no: res.data.data.accession_no || bookOption?.accession_no,
              book_status: res.data.data.book_status,
            },
          ],
        }));
        setScanBookIsbn("");
        setSelectedBookOption(null);
        setIsbnFieldError("");
      } else {
        setIsbnFieldError("Book not found");
      }
    } catch (err) {
      setIsbnFieldError("Book not found or no available copy exists");
    }
  };

  const handleRemoveBook = (index) => {
    setForm((prev) => ({
      ...prev,
      books: prev.books.filter((_, i) => i !== index),
    }));
  };

  const handleEdit = (row) => {
    setEditing(row);
    setForm({
      user_type_id: row.user_type_id || "",
      academic_year: row.academic_year || "",
      issue_type: row.issue_type || "SINGLE",
      entered_id: row.user_id || "",
      user_id: row.user_id || null,
      books: row.books || [],
    });
    setUserIdValidated(true);
    setResolvedMember({
      member_type: row.user_type || "",
      display_name: row.user_id || "",
      user_type_name: row.user_type || "",
      academic_year: row.academic_year || "",
    });
    setOpen(true);
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the issue record.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it!",
      customClass: {
        popup: "swal2-z-topmost",
      },
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/${row.issue_id}`
      );
      await Swal.fire("Deleted!", "The issue record has been deleted!", "success");
      fetchData();
    } catch (err) {
      await Swal.fire(
        "Sorry!",
        err?.response?.data?.message || "Delete failed. Please try again.",
        "error"
      );
    }
  };

  const handleSubmit = async () => {
    if (!userIdValidated && !editing) {
      setSnackbar({
        open: true,
        message: "Please enter a valid student roll number or staff code.",
        severity: "error",
      });
      return;
    }

    if (form.books.length === 0) {
      setSnackbar({
        open: true,
        message: "Please add at least one book to issue.",
        severity: "error",
      });
      return;
    }

    // Check issue limit
    if (rules && form.books.length > rules.available_quota) {
      setSnackbar({
        open: true,
        message: `Cannot issue ${form.books.length} books. Available quota: ${rules.available_quota}`,
        severity: "error",
      });
      return;
    }

    try {
      const today = new Date();
      const dueDate = rules?.nodays
        ? new Date(today.setDate(today.getDate() + rules.nodays))
        : new Date(today.setDate(today.getDate() + 14));

      if (editing) {
        await axios.put(
          `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/${editing.issue_id}`,
          {
            issue: {
              user_type_id: parseInt(form.user_type_id, 10),
              user_id: String(form.user_id || form.entered_id),
              issue_type: form.issue_type,
              due_date: dueDate,
              updated_by: user?.user_email || "system",
            },
            details: form.books.map((book) => ({
              college_id: parseInt(collegeId, 10),
              copy_id: book.copy_id,
              book_status: "ISSUED",
              created_by: user?.user_email || "system",
            })),
          }
        );
        setSnackbar({ open: true, message: "Updated successfully", severity: "success" });
      } else {
        const detailsPayload = form.books.map((b) => ({
          college_id: parseInt(collegeId, 10),
          copy_id: parseInt(b.copy_id, 10),
          book_status: "ISSUED",
          // delete_status: 0, // Removed: not supported by core_mstr_student
          created_by: user?.user_email || "system",
        }));

        const issuePayload = {
          academic_year: form.academic_year,
          issue: {
            user_type_id: parseInt(form.user_type_id),
            user_id: form.user_id,
            issue_type: form.issue_type,
            issue_date: new Date(),
            due_date: dueDate,
            college_id: parseInt(collegeId),
            created_by: user?.user_email || "system",
            // delete_status: 0, // Removed: not supported by core_mstr_student
          },
          details: detailsPayload,
        };

        await axios.post(
          `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}`,
          issuePayload
        );
        setSnackbar({ open: true, message: "Created successfully", severity: "success" });
      }
      setOpen(false);
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Save failed",
        severity: "error",
      });
    }
  };

  if (idError || isInvalidSession) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          {idError || "Session expired or invalid. Please log in again or contact admin."}
        </Alert>
      </Box>
    );
  }

  const filteredRows = Array.isArray(rows) ? rows : [];

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 90,
      sortable: false,
      renderCell: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "user_type", headerName: "User Type", flex: 1 },
    { field: "issue_type", headerName: "Issue Type", flex: 1 },
    { field: "user_id", headerName: "User ID", flex: 1 },
    {
      field: "issue_date",
      headerName: "Issue Date",
      flex: 1,
      valueGetter: (params) =>
        params.row.issue_date ? new Date(params.row.issue_date).toLocaleDateString() : "-",
    },
    {
      field: "due_date",
      headerName: "Due Date",
      flex: 1,
      valueGetter: (params) =>
        params.row.due_date ? new Date(params.row.due_date).toLocaleDateString() : "-",
    },
    {
      field: "books",
      headerName: "Books",
      flex: 1.4,
      minWidth: 220,
      valueGetter: (params) =>
        (params.row.books || []).map((b) => b.title || b.isbn || "").join(", "),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <ActionsMenu row={params.row} onEdit={handleEdit} onDelete={handleDelete} />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            LIBRARY BOOK ISSUES
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ backgroundColor: "#1976d2", color: "#fff", textTransform: "none", boxShadow: 1 }}
            onClick={handleAdd}
          >
            Issue Book
          </Button>
        </Toolbar>
        <Box sx={{ height: 500, mt: 2, ml: 1, mr: 1 }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            pagination
            pageSize={issuePageSize}
            onPageSizeChange={(newPageSize) => setIssuePageSize(newPageSize)}
            rowsPerPageOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            getRowId={(row) => row.issue_id}
          />
        </Box>
      </Paper>

      {/* Add/Edit Issue Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Edit Issue Book" : "Issue Book"}</DialogTitle>
        <DialogContent>
          {/* Rules Display Card */}
          <Card sx={{ mb: 3, mt: 1, bgcolor: "#f5f5f5" }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Issue Rules
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Issue Limit:
                  </Typography>
                  <Typography variant="h6">{rules?.issue_limit ?? "-"}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Available Quota:
                  </Typography>
                  <Typography
                    variant="h6"
                    color={rules?.available_quota > 0 ? "success.main" : "error.main"}
                  >
                    {rules?.available_quota ?? "-"}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Due Date:
                  </Typography>
                  <Typography variant="h6">
                    {rules?.nodays ? `${rules.nodays} days from issue` : "-"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Stack spacing={2} mt={1}>
            <TextField
              label="Student Roll Number / Staff Code"
              name="entered_id"
              value={form.entered_id}
              onChange={handleFormChange}
              onBlur={() => resolveEnteredId()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  resolveEnteredId();
                }
              }}
              fullWidth
              required
              error={!!idFieldError}
              helperText={
                idFieldError ||
                (resolvingMember
                  ? "Checking ID..."
                  : userIdValidated && "User validated successfully")
              }
              placeholder="Enter student roll number or staff code"
              sx={{ "& .MuiFormHelperText-root": { color: userIdValidated ? "green" : undefined } }}
            />

            {resolvedMember && (
              <Alert severity="success">
                {resolvedMember.user_type_name || resolvedMember.member_type}:{" "}
                {resolvedMember.display_name || resolvedMember.user_id} | Academic Year:{" "}
                {resolvedMember.academic_year || "-"}
                {resolvedMember.branch_name ? ` | Branch: ${resolvedMember.branch_name}` : ""}
              </Alert>
            )}

            {!editing && userIdValidated && !rules && (
              <Alert severity="warning">
                No issue rule is configured for this user, academic year, and issue type.
              </Alert>
            )}

            <TextField
              label="Issue Type"
              name="issue_type"
              value={form.issue_type}
              onChange={handleFormChange}
              select
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              <option value="SINGLE">Single</option>
              <option value="BULK">Bulk</option>
            </TextField>

            <Divider>Add Books</Divider>

            {(form.issue_type === "BULK" ||
              (form.issue_type === "SINGLE" && form.books.length < 1)) && (
              <Stack direction="row" spacing={2} alignItems="center">
                <Autocomplete
                  fullWidth
                  options={bookOptions}
                  value={selectedBookOption}
                  inputValue={scanBookIsbn}
                  loading={loadingBookOptions}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option.copy_id) === String(value.copy_id)
                  }
                  onInputChange={(_event, value) => {
                    setScanBookIsbn(value);
                    setIsbnFieldError("");
                  }}
                  onChange={(_event, value) => {
                    setSelectedBookOption(value);
                    setScanBookIsbn(value?.label || "");
                    setIsbnFieldError("");
                    if (form.issue_type === "SINGLE" && value) {
                      handleBookScan(value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Book"
                      error={!!isbnFieldError}
                      helperText={isbnFieldError}
                      placeholder="Search ISBN or book name"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingBookOptions ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.copy_id}>
                      <Box>
                        <Typography variant="body2">
                          {option.isbn} - {option.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Accession: {option.accession_no || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
                {form.issue_type === "BULK" && (
                  <Button
                    variant="contained"
                    color="inherit"
                    onClick={() => handleBookScan()}
                    sx={{ backgroundColor: "#fff", color: "#000", boxShadow: 1, minWidth: 120 }}
                  >
                    Add Book
                  </Button>
                )}
              </Stack>
            )}

            {form.issue_type === "SINGLE" && form.books.length >= 1 && (
              <Alert severity="info">Single issue type: Only one book can be added.</Alert>
            )}

            {/* Books List */}
            {form.books.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Books to Issue ({form.books.length}):
                </Typography>
                <Stack spacing={1}>
                  {form.books.map((book, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2">
                            <strong>{book.title || "Book"}</strong>
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ISBN: {book.isbn} | Accession: {book.accession_no || "N/A"}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveBook(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (
                !form.user_type_id ||
                (!editing && !form.academic_year) ||
                !form.issue_type ||
                !form.entered_id ||
                !userIdValidated ||
                (!editing && !rules) ||
                form.books.length === 0 ||
                (form.issue_type === "SINGLE" && form.books.length > 1)
              ) {
                alert(
                  `Disabled because:\n` +
                    (!form.user_type_id ? "- User type was not auto-detected\n" : "") +
                    (!editing && !form.academic_year
                      ? "- Active academic year was not found\n"
                      : "") +
                    (!form.issue_type ? "- Issue Type is missing\n" : "") +
                    (!form.entered_id ? "- Roll Number/Staff Code is missing\n" : "") +
                    (!userIdValidated ? "- User ID is not validated\n" : "") +
                    (!editing && !rules ? "- Matching issue rule is not configured\n" : "") +
                    (form.books.length === 0 ? "- No books added\n" : "") +
                    (form.issue_type === "SINGLE" && form.books.length > 1
                      ? "- Only one book allowed for Single issue type\n"
                      : "")
                );
                return;
              }
              handleSubmit();
            }}
            disabled={
              !form.user_type_id ||
              (!editing && !form.academic_year) ||
              !form.issue_type ||
              !form.entered_id ||
              !userIdValidated ||
              (!editing && !rules) ||
              form.books.length === 0 ||
              (form.issue_type === "SINGLE" && form.books.length > 1) ||
              (rules && rules.available_quota <= 0)
            }
            variant="contained"
            color="primary"
            sx={{
              color: "#fff",
              "&.Mui-disabled": {
                color: "#fff",
              },
            }}
          >
            {editing ? "Update Issued Book" : "Issue Book"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
